/* 

    Payment application helper functions.

*/

// Get parent origin, this is required when doing a postMessage.
const parentOrigin = window.parent.location.origin;

// Attributes used for query selectors.
const paymentSelectorAttibutes = {
    "token-input": "data-token",
    "card-number-input": "data-card-number",
    "card-network-input": "data-card-network",
    "client-colour-depth": "data-client-colour-depth",
    "client-screen-height": "data-client-screen-width",
    "client-screen-width": "data-client-screen-height",
    "client-time-zone": "data-client-time-zone",
    "challenge-window-size-input": "data-client-challenge-window-size",
    "client-language-input": "data-client-language"
}

/* 
    Dimensions of the challenge window that has been displayed to the Cardholder, width x height in pixels
    https://developer.elavon.com/products/en-uk/opayo/v1/api-reference#tag/Transactions/operation/createTransaction
*/  
const challangeWindowDimensions = [
    {
        "width": 250,
        "height": 400,
        "description": "Small"
    },
    {
        "width": 390,
        "height": 400,
        "description": "Medium"
    },
    {
        "width": 500,
        "height": 600,
        "description": "Large"
    },
    {
        "width": 600,
        "height": 400,
        "description": "ExtraLarge"
    },
    {
        "width": Infinity,
        "height": Infinity,
        "description": "FullScreen"
    }
];

// Fetch script.
const fetchScript = (url) => {
    return new Promise((resolve, reject) => {
        // Create script element + add to DOM.
        const script = document.createElement('script');
        //document.body.appendChild(script);
        // Configuration.
        script.src = url;
        script.async = false;
        document.head.appendChild(script);
        // Success
        script.onload = () => resolve();
        // Error.
        script.onerror = () => reject();
    })
}

// Send Cross Window Message.
const sendWindowMessage = (message, targetOrigin) => {
    if (!message) {
        const error = new Error("No message provided");
        return error;
    }
    // If no target origin provided then fallback to parent.
    if (!targetOrigin) {
        targetOrigin = parentOrigin;
    }
    window.parent.postMessage(message, parentOrigin);
    return true;
}

// Generate base style block.
const generateBaseStyleBlock = () => {
    // Create containing element.
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    // Content.
    style.textContent = `
        body {
            margin: 0;
            padding: 0;
        }
    `;
    // Update DOM.
    document.head.append(style);
}

// Get containing frame context.
const containerFrame = () => {
    const frame = window.frameElement;
    return frame;
}

// Get containing frame id.
const containerFrameId = () => {
    const frame = containerFrame();
    if (frame) {
        const id = frame.getAttribute('id');
        if (!id) {
            return new Error("Containing iframe has no Id");
        } else {
            return id;
        }
    } else {
        return new Error("Failed to get iframe context.");
    }
}

// Get pay button dimensions.
const payButtonDimensions = (button) => {
    if (!button) {
        const error = new Error("No button context provided")
        return error;
    }
    // Stores width + height.
    const dimensions = new Object();
    // Get width.
    const width = button.offsetWidth;
    dimensions['width'] = width;
    // Get height.
    const height = button.offsetHeight;
    dimensions['height'] = height
    return dimensions;
}

// Strong Customer Authentication client information.
const strongCustomerAuthenticationClientInformation = () => {
    const data = {};
    // Get screen colour depth.
    const clientColourDepth = screen.colorDepth;
    data['colour-depth'] = clientColourDepth;
    // Screen width.
    const screenWidth = screen.width;
    data['screen-width'] = screenWidth;
    // Screen height.
    const screenHeight = screen.height;
    data['screen-height'] = screenHeight;
    // Time-zone offset.
    const timeZoneOffset = new Date().getTimezoneOffset();
    data['time-zone'] = timeZoneOffset;
    // 3D Secure Challenge Window Size.
    const challangeWindowSize = challangeWindowDimensions[2]['description'];
    data['challenge-window-size'] = challangeWindowSize;
    // Language.
    const language = navigator.language;
    data['language'] = language;
    return data;
}

function getClosestChallengeSize(containerWidth) {
    // If viewport width is more than 500 then default to large.
    if (containerWidth >= 640) {
        return challangeWindowDimensions.find(d => d.description === 'Large');
    }
    // Filter out dimension(s).
    const dimensions = challangeWindowDimensions.filter(d => isFinite(d.width));
    // Only consider dimensions that fit within the viewport.
    const fittingDimensions = dimensions.filter(d => d.width <= containerWidth);
    // If nothing fits, fall back to the smallest.
    if (!fittingDimensions.length) {
        return dimensions.reduce((smallest, current) => current.width < smallest.width ? current : smallest);
    }
    // Pick the largest that still fits.
    const closestDimension = fittingDimensions.reduce((closest, current) => {
        return current.width > closest.width ? current : closest;
    });
    return closestDimension;
}

export {
    fetchScript,
    sendWindowMessage,
    generateBaseStyleBlock,
    containerFrame,
    containerFrameId,
    payButtonDimensions,
    strongCustomerAuthenticationClientInformation,
    paymentSelectorAttibutes,
    challangeWindowDimensions,
    getClosestChallengeSize
}